import {useState, useEffect, useRef, useMemo, useCallback} from 'react'

/**
 * GA4 Viewport Tracking Hook
 *
 * Implements GA4-compliant viewport tracking with threshold approach:
 * - Initial load: 50% of products must be visible before triggering
 * - Scroll: Any new product triggers immediately
 * - Deduplication: Tracks sent products to prevent duplicates
 * - Persistence: Maintains state during pagination within same category
 *
 * @param {Array} products - Array of product objects with productId
 * @param {Function} onViewItemList - Callback when products should be sent to GA4
 * @param {string} categoryId - Current category ID for context tracking
 * @param {string} searchQuery - Current search query for context tracking
 * @param {number} visibilityThreshold - Threshold for initial load (default: 0.5)
 * @param {boolean} enabled - When false, visibility is still tracked but GA4 firing is
 *   deferred (e.g. while PLP prices are still being enriched with promotions), so GA4
 *   never captures a pre-enrichment price.
 */
export const useViewportTracking = ({
    products = [],
    onViewItemList,
    categoryId,
    searchQuery,
    visibilityThreshold = 0.5,
    enabled = true
}) => {
    // Local state tracking - resets on component unmount (PDP return)
    const [sentProducts, setSentProducts] = useState(new Set())
    const [visibleProducts, setVisibleProducts] = useState(new Set())
    const observerRef = useRef(null)
    const currentProductIdsRef = useRef(new Set())

    // Setup IntersectionObserver with optimized settings
    // Deferred to idle time to reduce TBT (Total Blocking Time)
    useEffect(() => {
        // Use requestIdleCallback to defer observer creation, with fallback for Safari
        const idleCallback = window.requestIdleCallback || ((cb) => setTimeout(cb, 100))
        const cancelIdleCallback = window.cancelIdleCallback || clearTimeout

        const handle = idleCallback(() => {
            if (!observerRef.current) {
                observerRef.current = new IntersectionObserver(
                    (entries) => {
                        setVisibleProducts((prev) => {
                            const newVisibleProducts = new Set(prev)

                            entries.forEach((entry) => {
                                const productId = entry.target.dataset.productId

                                if (entry.isIntersecting && productId) {
                                    newVisibleProducts.add(productId)
                                } else if (productId) {
                                    newVisibleProducts.delete(productId)
                                }
                            })

                            return newVisibleProducts
                        })
                    },
                    {
                        threshold: 0.1, // Trigger when 10% of product is visible
                        rootMargin: '0px 0px -10% 0px' // 10% margin from bottom for better UX
                    }
                )
            }
        })

        return () => {
            cancelIdleCallback(handle)
            if (observerRef.current) {
                observerRef.current.disconnect()
            }
        }
    }, [])

    // Track when new products are loaded - but DON'T filter sentProducts
    // Keep the full history of sent products regardless of current products array
    useEffect(() => {
        const newProductIds = new Set(products.map((p) => p.productId))
        currentProductIdsRef.current = newProductIds
    }, [products])

    // Stable dependencies to prevent unnecessary re-renders
    const visibleProductsArray = useMemo(() => Array.from(visibleProducts), [visibleProducts])
    const productsIds = useMemo(() => products.map((p) => p.productId).join(','), [products])

    // GA4 Event triggering logic with threshold approach
    useEffect(() => {
        if (!enabled) return
        if (products.length > 0 && visibleProducts.size > 0) {
            const newVisibleProducts = visibleProductsArray.filter(
                (productId) => !sentProducts.has(productId)
            )

            if (newVisibleProducts.length > 0) {
                // GA4 Threshold Logic:
                // - Initial load: require 50% visibility threshold
                // - Subsequent scrolls: send any new products immediately
                const isInitialLoad = sentProducts.size === 0
                const shouldTrigger = isInitialLoad
                    ? newVisibleProducts.length / Math.max(visibleProducts.size, 1) >=
                      visibilityThreshold
                    : newVisibleProducts.length > 0

                if (shouldTrigger) {
                    const newVisibleProductsArray = products.filter((product) =>
                        newVisibleProducts.includes(product.productId)
                    )

                    onViewItemList(newVisibleProductsArray)
                    setSentProducts((prev) => new Set([...prev, ...newVisibleProducts]))
                }
            }
        }
    }, [visibleProductsArray, productsIds, visibilityThreshold, onViewItemList, enabled])

    // Reset tracking when category/search changes
    const contextKey = `${categoryId || 'search'}-${searchQuery || 'no-query'}`
    const prevContextRef = useRef(contextKey)
    useEffect(() => {
        if (prevContextRef.current !== contextKey) {
            setVisibleProducts(new Set())
            setSentProducts(new Set())
            currentProductIdsRef.current = new Set()

            if (observerRef.current) {
                observerRef.current.disconnect()
            }

            prevContextRef.current = contextKey
        }
    }, [contextKey])

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (observerRef.current) {
                observerRef.current.disconnect()
            }
        }
    }, [])

    // Function to observe a product element
    const observeProduct = (element) => {
        if (element && observerRef.current) {
            observerRef.current.observe(element)
        }
    }

    return {
        observeProduct,
        // Optional: expose state for debugging only
        visibleProducts: Array.from(visibleProducts),
        sentProducts: Array.from(sentProducts)
    }
}
